/**
 * Accept records: "the operator decided this finding is fine" (P3).
 *
 * WHY A DIGEST PATH. `store.update(path, …)` resolves through `setByPath`,
 * which splits on `.`. An acceptKey contains a checkId, a target that may hold
 * a domain or a CSS selector, and a fingerprint — feed that to `setByPath` and
 * it silently builds a tree of nested objects instead of one record. So the
 * STORAGE key is a digest of the acceptKey, and the key itself lives inside the
 * record where it can hold any character safely.
 *
 * The other reason for a per-record path: a collaborator accepting one finding
 * patches ONE path. Writing the whole map would make two people accepting two
 * different findings a last-write-wins race that loses one of the decisions.
 *
 * Size limits are enforced here rather than at the UI, because a note arrives
 * from a text field and an accept map is versioned project data that travels in
 * every save, every collab frame and every export lock.
 */

import { store } from './state.js';
import { sha256Hex } from '../shared/site-deploy.js';
import {
  MAX_ACCEPT_BY_CHARS,
  MAX_ACCEPT_KEY_CHARS,
  MAX_ACCEPT_NOTE_CHARS,
  MAX_ACCEPT_RECORDS,
  isValidAcceptKey
} from '../shared/audit-accept-schema.js';

/** Max characters kept from an operator's note. */
export const MAX_NOTE_CHARS = MAX_ACCEPT_NOTE_CHARS;
/** Refuse to grow the map beyond this; a runaway map bloats every save. */
export const MAX_ACCEPTS = MAX_ACCEPT_RECORDS;
export { MAX_ACCEPT_KEY_CHARS };

const LABEL = 'Accept audit finding';
const UNACCEPT_LABEL = 'Reopen audit finding';

/**
 * Storage path segment for an acceptKey. Hex only, so it can never introduce a
 * `.` into a state path.
 */
export async function acceptDigest(acceptKey) {
  return sha256Hex(String(acceptKey || ''));
}

/** True when this finding can be accepted at all. */
export function isAcceptable(finding) {
  return !!(finding && isValidAcceptKey(finding.acceptKey));
}

/**
 * Record an accept. Returns false when the finding carries no acceptKey — the
 * caller must not offer an Accept affordance for such a finding, and this is
 * the backstop if it does.
 */
export async function acceptFinding(finding, { by = null, note = null, now = Date.now() } = {}) {
  if (!isAcceptable(finding)) return false;
  const state = store.getState();
  const existing = state?.auditAccepts || {};
  const digest = await acceptDigest(finding.acceptKey);
  if (!Object.prototype.hasOwnProperty.call(existing, digest)) {
    if (Object.keys(existing).length >= MAX_ACCEPTS) return false;
  }
  store.update(
    `auditAccepts.${digest}`,
    {
      key: finding.acceptKey,
      by: by ? String(by).slice(0, MAX_ACCEPT_BY_CHARS) : null,
      note: note ? String(note).slice(0, MAX_NOTE_CHARS) : null,
      at: now
    },
    LABEL
  );
  return true;
}

/**
 * Reopen a previously accepted finding. Writes `null` rather than deleting the
 * path: a delete would have to rewrite the whole map, which reintroduces the
 * lost-update race the per-record path exists to avoid. The loaders and the
 * disposition stage both ignore records with no string `key`.
 */
export async function unacceptFinding(finding) {
  if (!isAcceptable(finding)) return false;
  const digest = await acceptDigest(finding.acceptKey);
  const existing = store.getState()?.auditAccepts || {};
  if (!Object.prototype.hasOwnProperty.call(existing, digest)) return false;
  store.update(`auditAccepts.${digest}`, null, UNACCEPT_LABEL);
  return true;
}
